import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNotificationContext } from '../contexts/NotificationContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import useCascadeDelete from '../hooks/useCascadeDelete';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const Courses = () => {
  const { showSuccess, showError } = useNotificationContext();
  const { deleteCourse, loading: deleteLoading } = useCascadeDelete();
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [departments, setDepartments] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [availableTracks, setAvailableTracks] = useState([]);
  const [filteredTracks, setFilteredTracks] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, courseId: null, courseName: '' });
  const [filters, setFilters] = useState({
    department: '',
    semester: '',
    academicYear: '',
    track: ''
  });
  const [viewMode, setViewMode] = useState('grid');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    semester: 1,
    level: 1,
    academicYear: '',
    hours: {
      lectures: 0,
      tutorials: 0,
      practicals: 0
    },
    department: '',
    track: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Update filtered tracks when department or academic year filter changes
  useEffect(() => {
    if ((filters.department || filters.academicYear) && tracks.length > 0) {
      const deptTracks = tracks.filter(track => {
        const deptMatch = !filters.department || 
          track.department === filters.department || 
          track.department?._id === filters.department;
        const yearMatch = !filters.academicYear || 
          track.academicYear === filters.academicYear || 
          track.academicYear?._id === filters.academicYear;
        return deptMatch && yearMatch;
      });
      setFilteredTracks(deptTracks);
      // Reset track filter if current selection is not in filtered list
      if (filters.track && !deptTracks.some(t => t._id === filters.track)) {
        setFilters(prev => ({ ...prev, track: '' }));
      }
    } else {
      setFilteredTracks(tracks);
    }
  }, [filters.department, filters.academicYear, tracks]);

  const handleWebSocketMessage = useCallback((data) => {
    if (data.type === 'connection') {
      console.log('Connection message:', data.message);
    } else if (data.type === 'course_updated') {
      console.log('🔄 Course update reçu:', data.data?.action);
      fetchData();
    }
  }, []);

  const { isConnected } = useWebSocket('courses', handleWebSocketMessage);

  const fetchData = async () => {
    try {
      const timestamp = Date.now();
      const [coursesRes, departmentsRes, academicYearsRes, tracksRes] = await Promise.all([
        axios.get(`${API_URL}/api/courses?limit=1000&_t=${timestamp}`),
        axios.get(`${API_URL}/api/departments?limit=1000&_t=${timestamp}`),
        axios.get(`${API_URL}/api/academic-years?limit=1000&_t=${timestamp}`),
        axios.get(`${API_URL}/api/tracks?limit=1000&_t=${timestamp}`)
      ]);
      
      setCourses(coursesRes.data);
      setDepartments(departmentsRes.data);
      setAcademicYears(academicYearsRes.data);
      setTracks(Array.isArray(tracksRes.data) ? tracksRes.data : tracksRes.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      if (editingCourse) {
        await axios.put(`${API_URL}/api/courses/${editingCourse._id}`, formData, config);
      } else {
        await axios.post(`${API_URL}/api/courses`, formData, config);
      }
      closeModal();
      showSuccess(editingCourse ? 'Cours modifié avec succès' : 'Cours ajouté avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showError(error.response?.data?.message || 'Erreur lors de la sauvegarde du cours');
    }
  };

  const confirmDelete = (course) => {
    setDeleteConfirm({ 
      show: true, 
      courseId: course._id, 
      courseName: course.name 
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm.courseId) {
      await deleteCourse(deleteConfirm.courseId);
      fetchData(); // Refresh the list
    }
    setDeleteConfirm({ show: false, courseId: null, courseName: '' });
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, courseId: null, courseName: '' });
  };

  const openModal = async (course = null) => {
    setEditingCourse(course);
    
    if (course) {
      const departmentId = course.department?._id || '';
      setFormData({
        name: course.name,
        code: course.code,
        semester: course.semester,
        level: course.level || 1,
        academicYear: course.academicYear?._id || course.academicYear || '',
        hours: course.hours || { lectures: 0, tutorials: 0, practicals: 0 },
        department: departmentId,
        track: course.track?._id || course.track || ''
      });
      
      if (departmentId) {
        try {
          const academicYearId = course.academicYear?._id || course.academicYear;
          let url = `${API_URL}/api/tracks?department=${departmentId}`;
          if (academicYearId) {
            url += `&academicYear=${academicYearId}`;
          }
          const response = await axios.get(url);
          setAvailableTracks(response.data);
        } catch (error) {
          console.error('Erreur lors du chargement des parcours:', error);
          setAvailableTracks([]);
        }
      }
    } else {
      setFormData({
        name: '',
        code: '',
        semester: 1,
        level: 1,
        academicYear: academicYears.length > 0 ? academicYears[0]._id : '',
        hours: { lectures: 0, tutorials: 0, practicals: 0 },
        department: departments.length > 0 ? departments[0]._id : '',
        track: ''
      });
      
      if (departments.length > 0) {
        try {
          const defaultDeptId = departments[0]._id;
          const defaultYearId = academicYears.length > 0 ? academicYears[0]._id : '';
          let url = `${API_URL}/api/tracks?department=${defaultDeptId}`;
          if (defaultYearId) {
            url += `&academicYear=${defaultYearId}`;
          }
          const response = await axios.get(url);
          setAvailableTracks(response.data);
        } catch (error) {
          console.error('Erreur lors du chargement des parcours:', error);
          setAvailableTracks([]);
        }
      }
    }
    
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCourse(null);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('hours.')) {
      const hourType = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        hours: {
          ...prev.hours,
          [hourType]: parseFloat(value) || 0
        }
      }));
    } else {
      const newValue = ['semester', 'level'].includes(name) ? Number(value) : value;
      setFormData(prev => ({
        ...prev,
        [name]: newValue
      }));
      
      if ((name === 'department' || name === 'academicYear') && value) {
        try {
          const deptId = name === 'department' ? value : formData.department;
          const yearId = name === 'academicYear' ? value : formData.academicYear;
          
          let url = `${API_URL}/api/tracks?limit=1000`;
          if (deptId) url += `&department=${deptId}`;
          if (yearId) url += `&academicYear=${yearId}`;
          
          const response = await axios.get(url);
          const tracksData = Array.isArray(response.data) ? response.data : response.data.data || [];
          setAvailableTracks(tracksData);
          
          setFormData(prev => ({
            ...prev,
            track: tracksData.length > 0 ? tracksData[0]._id : ''
          }));
        } catch (error) {
          console.error('Erreur lors du chargement des parcours:', error);
          setAvailableTracks([]);
        }
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: name === 'semester' ? (value ? Number(value) : '') : value
    });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredCourses = courses.filter(course => {
    let match = true;
    
    if (filters.department && course.department) {
      match = match && (course.department._id === filters.department);
    }
    
    if (filters.semester && course.semester) {
      match = match && (course.semester === filters.semester);
    }
    
    if (filters.academicYear && course.academicYear) {
      match = match && (course.academicYear._id === filters.academicYear);
    }

    if (filters.track && course.track) {
      match = match && (course.track._id === filters.track || course.track === filters.track);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      match = match && (
        course.name.toLowerCase().includes(term) ||
        course.code.toLowerCase().includes(term) ||
        course.department?.name.toLowerCase().includes(term) ||
        course.track?.name.toLowerCase().includes(term)
      );
    }
    
    return match;
  });

  const getTotalHours = (hours) => {
    return (hours?.lectures || 0) + (hours?.tutorials || 0) + (hours?.practicals || 0);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-600">Chargement...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <h1 className="text-xl font-semibold text-gray-900">Cours</h1>
            <span className={`px-2 py-1 text-xs rounded-full ${
              isConnected 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              {isConnected ? '🟢 Live' : '🔴 Manual'}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={handleSearch}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <div className="flex gap-2">
              <button 
                className={`px-3 py-2 text-xs font-medium rounded-lg ${
                  viewMode === 'grid' 
                    ? 'bg-primary-100 text-primary-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setViewMode('grid')}
              >
                📊 Grille
              </button>
              <button 
                className={`px-3 py-2 text-xs font-medium rounded-lg ${
                  viewMode === 'table' 
                    ? 'bg-primary-100 text-primary-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setViewMode('table')}
              >
                📋 Tableau
              </button>
            </div>
            <button 
              onClick={() => openModal()} 
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
              disabled={departments.length === 0}
            >
              + Ajouter
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mb-6">
          <select 
            name="department" 
            value={filters.department} 
            onChange={handleFilterChange}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Tous départements</option>
            {departments.map(dept => (
              <option key={dept._id} value={dept._id}>{dept.name}</option>
            ))}
          </select>
          <select 
            name="semester" 
            value={filters.semester} 
            onChange={handleFilterChange}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Tous semestres</option>
            {[1, 2].map(sem => (
              <option key={sem} value={sem}>Semestre {sem}</option>
            ))}
          </select>
          <select 
            name="academicYear" 
            value={filters.academicYear} 
            onChange={handleFilterChange}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Toutes années</option>
            {academicYears.map(year => (
              <option key={year._id} value={year._id}>{year.name}</option>
            ))}
          </select>
          <select 
            name="track" 
            value={filters.track} 
            onChange={handleFilterChange}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={!filters.department && !filters.academicYear}
          >
            <option value="">Toutes filières</option>
            {filteredTracks.map(track => (
              <option key={track._id} value={track._id}>{track.name}</option>
            ))}
          </select>
          {(filters.department || filters.semester || filters.academicYear || filters.track) && (
            <button 
              onClick={() => setFilters({ department: '', semester: '', academicYear: '', track: '' })}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Réinitialiser
            </button>
          )}
        </div>

        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredCourses.map(course => (
              <div key={course._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-medium text-gray-900 text-sm">{course.name}</h3>
                  <span className="px-2 py-1 text-xs bg-primary-100 text-primary-700 rounded">
                    {course.code}
                  </span>
                </div>
                <div className="space-y-1 text-xs text-gray-600 mb-4">
                  <p><span className="font-medium">Semestre:</span> {course.semester}</p>
                  <p><span className="font-medium">Année:</span> {course.academicYear?.name || 'N/A'}</p>
                  <p><span className="font-medium">Dépt:</span> {course.department?.name || 'N/A'}</p>
                  <div className="flex gap-2 text-xs">
                    <span>C: {course.hours?.lectures || 0}h</span>
                    <span>TD: {course.hours?.tutorials || 0}h</span>
                    <span>TP: {course.hours?.practicals || 0}h</span>
                  </div>
                  <p><span className="font-medium">Total:</span> {getTotalHours(course.hours)}h</p>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => openModal(course)}
                    className="flex-1 px-3 py-1 text-xs font-medium text-primary-700 bg-primary-50 rounded hover:bg-primary-100"
                  >
                    Modifier
                  </button>
                  <button 
                    onClick={() => confirmDelete(course)}
                    className="px-3 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100"
                  >
                    Suppr.
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">S</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Année</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dépt</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">C</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">TD</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">TP</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredCourses.map(course => (
                    <tr key={course._id}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{course.code}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{course.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">S{course.semester}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{course.academicYear?.name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{course.department?.name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{course.hours?.lectures || 0}h</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{course.hours?.tutorials || 0}h</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{course.hours?.practicals || 0}h</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{getTotalHours(course.hours)}h</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button 
                            onClick={() => openModal(course)}
                            className="px-2 py-1 text-xs font-medium text-primary-700 bg-primary-50 rounded hover:bg-primary-100"
                          >
                            ✏️
                          </button>
                          <button 
                            onClick={() => confirmDelete(course)}
                            className="px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {filteredCourses.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm">
              Aucun cours trouvé. {courses.length > 0 ? 'Modifiez les filtres.' : 'Ajoutez un cours pour commencer.'}
            </p>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-gray-900">{editingCourse ? 'Modifier Cours' : 'Ajouter Cours'}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom du cours</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code du cours</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Semestre</label>
                    <select
                      name="semester"
                      value={formData.semester}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      {[1, 2].map(sem => (
                        <option key={sem} value={sem}>Semestre {sem}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Niveau</label>
                    <select
                      name="level"
                      value={formData.level}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      {[1, 2, 3].map(level => (
                        <option key={level} value={level}>{level}ère année</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Année universitaire</label>
                  <select
                    name="academicYear"
                    value={formData.academicYear}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Sélectionner une année</option>
                    {academicYears.map(year => (
                      <option key={year._id} value={year._id}>{year.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Sélectionner un département</option>
                    {departments.map(dept => (
                      <option key={dept._id} value={dept._id}>{dept.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Filière</label>
                  <select
                    name="track"
                    value={formData.track}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                    required
                    disabled={!formData.department}
                  >
                    <option value="">Sélectionner une filière</option>
                    {availableTracks.map(track => (
                      <option key={track._id} value={track._id}>{track.name} ({track.code})</option>
                    ))}
                  </select>
                  {!formData.department && (
                    <p className="text-xs text-gray-500 mt-1">Sélectionnez d'abord un département</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heures d'enseignement</label>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Cours</label>
                      <input
                        type="number"
                        name="hours.lectures"
                        value={formData.hours.lectures}
                        onChange={handleChange}
                        min="0"
                        step="0.5"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">TD</label>
                      <input
                        type="number"
                        name="hours.tutorials"
                        value={formData.hours.tutorials}
                        onChange={handleChange}
                        min="0"
                        step="0.5"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">TP</label>
                      <input
                        type="number"
                        name="hours.practicals"
                        value={formData.hours.practicals}
                        onChange={handleChange}
                        min="0"
                        step="0.5"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button 
                    type="submit" 
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
                  >
                    {editingCourse ? 'Modifier' : 'Ajouter'}
                  </button>
                  <button 
                    type="button" 
                    onClick={closeModal} 
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <DeleteConfirmationModal
          isOpen={deleteConfirm.show}
          onClose={cancelDelete}
          onConfirm={handleConfirmDelete}
          title="Supprimer le cours"
          message={`Êtes-vous sûr de vouloir supprimer le cours ${deleteConfirm.courseName} ? Toutes les séances associées seront également supprimées.`}
          loading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default Courses;